import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCircleInfo,
    faMagnifyingGlass,
    faPen,
    faPlus,
    faPowerOff,
    faTriangleExclamation,
    faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { useFlashMessage } from '../FlashMessage';
import { formatAmount } from '../../utils/amountFormat';
import {
    activateInstrument,
    deactivateInstrument,
    fetchInstruments,
} from '../../services/paperPricingService';
import { formatCount, refusalText, unitLabel } from './paperPricingFormat';
import { ActiveStatus, Dash, LoadProblem, textOrDash } from './PaperPricingParts';
import useMasterList from './useMasterList';
import InstrumentFormModal from './InstrumentFormModal';

const COLUMNS = 6;

// Every instrument names its benchmark; a row without one is a broken answer,
// not an instrument that settles off nothing.
const checkInstrument = (instrument) => {
    if (!instrument.benchmark || !instrument.benchmark.code) {
        throw new Error('The server sent an answer this screen cannot read. Refresh the page and try again.');
    }
};

const InstrumentDetail = ({ instrument, acting, actionError, onToggle, onEdit, onClose }) => {
    const { benchmark } = instrument;
    const rounding = instrument.rounding_decimals;
    return (
        <aside className="pp-detail" aria-label={`${instrument.name} details`}>
            <div className="pp-detail-head">
                <div className="pp-detail-titles">
                    <div className="pp-detail-eyebrow">Paper instrument</div>
                    <h3 className="pp-detail-title">{instrument.name}</h3>
                    <div className="pp-detail-sub">
                        <ActiveStatus active={instrument.is_active} />
                    </div>
                </div>
                <button type="button" className="pp-icon-btn" aria-label="Close details" onClick={onClose}>
                    <FontAwesomeIcon icon={faXmark} />
                </button>
            </div>

            <dl className="pp-detail-list">
                <dt>Instrument type</dt>
                <dd>{textOrDash(instrument.instrument_type_label)}</dd>
                <dt>Underlying index</dt>
                <dd><span className="pp-code">{benchmark.code}</span></dd>
                <dt>Currency</dt>
                <dd>{textOrDash(instrument.currency)}</dd>
                <dt>Contract unit</dt>
                <dd>{textOrDash(instrument.contract_unit)}</dd>
                <dt>Settlement basis</dt>
                <dd>{textOrDash(instrument.settlement_basis)}</dd>
                <dt>Contract periods</dt>
                <dd>{textOrDash(instrument.contract_period_label)}</dd>
                <dt>Cash or physical</dt>
                <dd>{textOrDash(instrument.settlement_type_label)}</dd>
                <dt>Minimum quantity</dt>
                <dd>
                    {instrument.minimum_quantity === null || instrument.minimum_quantity === undefined
                        ? <Dash />
                        : `${formatAmount(instrument.minimum_quantity)} ${instrument.contract_unit}`}
                </dd>
                <dt>Rounding</dt>
                <dd>{`${rounding} ${rounding === 1 ? 'decimal' : 'decimals'}`}</dd>
            </dl>

            <div className="pp-detail-section">
                <div className="pp-detail-section-title">Underlying benchmark</div>
                <div className="pp-detail-benchmark">
                    <span className="pp-code">{benchmark.code}</span>
                    <span>{benchmark.name}</span>
                    <ActiveStatus active={benchmark.is_active} />
                </div>
                {benchmark.is_active ? null : (
                    <div className="pp-alert pp-alert--warn">
                        <FontAwesomeIcon icon={faTriangleExclamation} className="pp-alert-icon" />
                        <span className="pp-alert-text">
                            {benchmark.code} is switched off. Switch it back on, or move this instrument to an
                            active benchmark with Edit.
                        </span>
                    </div>
                )}
            </div>

            {actionError ? <div className="pp-alert pp-alert--error" role="alert">{actionError}</div> : null}

            <div className="pp-detail-actions">
                <button type="button" className="pp-btn pp-btn--secondary" onClick={onToggle} disabled={acting}>
                    <FontAwesomeIcon icon={faPowerOff} /> {instrument.is_active ? 'Deactivate' : 'Activate'}
                </button>
                <button type="button" className="pp-btn pp-btn--primary" onClick={onEdit} disabled={acting}>
                    <FontAwesomeIcon icon={faPen} /> Edit
                </button>
            </div>
        </aside>
    );
};

/**
 * Swap & futures instruments (ledger row 132, AC3): the paper contracts the desk
 * hedges with. Each settles off one benchmark, which must be active -- the
 * server refuses an instrument on a switched-off benchmark, and refuses to
 * switch an instrument on while its benchmark is off.
 */
const PaperInstruments = () => {
    const { showSuccess } = useFlashMessage();
    const { search, setSearch, list, loading, error, reload } = useMasterList(
        fetchInstruments,
        'The instrument list could not be loaded. Try again.',
        checkInstrument,
    );
    const [selectedId, setSelectedId] = useState(null);
    const [form, setForm] = useState(null);
    const [acting, setActing] = useState(false);
    const [actionError, setActionError] = useState('');

    const results = list ? list.results : [];
    const selected = results.find((instrument) => instrument.id === selectedId) || null;

    const select = (id) => {
        setActionError('');
        setSelectedId((current) => (current === id ? null : id));
    };

    const toggleActive = async (instrument) => {
        setActing(true);
        setActionError('');
        try {
            const updated = instrument.is_active
                ? await deactivateInstrument(instrument.id)
                : await activateInstrument(instrument.id);
            showSuccess(`${updated.name} is now ${updated.is_active ? 'active' : 'inactive'}.`);
            reload();
        } catch (err) {
            setActionError(refusalText(
                err,
                `${instrument.name} could not be ${instrument.is_active ? 'deactivated' : 'activated'}. Try again.`,
            ));
        } finally {
            setActing(false);
        }
    };

    const handleSaved = (saved, wasEditing) => {
        setForm(null);
        showSuccess(wasEditing ? `Saved ${saved.name}.` : `Added ${saved.name} to the instrument list.`);
        setActionError('');
        setSelectedId(saved.id);
        reload();
    };

    const emptyText = search.trim()
        ? 'No instrument matches your search.'
        : 'No paper instruments yet. Add the first one with Add instrument.';

    return (
        <>
            <div className="pp-toolbar">
                <div className="pp-search">
                    <FontAwesomeIcon icon={faMagnifyingGlass} className="pp-search-icon" />
                    <input
                        type="search"
                        className="pp-input pp-search-input"
                        placeholder="Instrument or underlying benchmark"
                        aria-label="Search instruments"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                    />
                </div>
                <span className="pp-count">
                    {list ? `${formatCount(list.counts.active)} active · ${formatCount(list.counts.inactive)} inactive` : ''}
                </span>
                <button
                    type="button"
                    className="pp-btn pp-btn--primary pp-toolbar-action"
                    onClick={() => setForm({ mode: 'add' })}
                >
                    <FontAwesomeIcon icon={faPlus} /> Add instrument
                </button>
            </div>

            {error ? (
                <LoadProblem message={error} onRetry={reload} />
            ) : (
                <div className={`pp-master ${selected ? 'pp-master--with-detail' : ''}`}>
                    <div className={`pp-table-scroll ${loading && list ? 'pp-dim' : ''}`}>
                        <table className="pp-table pp-table--master">
                            <thead>
                                <tr>
                                    <th scope="col">Instrument</th>
                                    <th scope="col">Type</th>
                                    <th scope="col">Underlying</th>
                                    <th scope="col">Unit</th>
                                    <th scope="col">Settlement</th>
                                    <th scope="col">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && !list ? (
                                    <tr><td colSpan={COLUMNS} className="pp-state">Loading instruments…</td></tr>
                                ) : results.length === 0 ? (
                                    <tr><td colSpan={COLUMNS} className="pp-state">{emptyText}</td></tr>
                                ) : results.map((instrument) => (
                                    <tr
                                        key={instrument.id}
                                        className={[
                                            'pp-row--clickable',
                                            instrument.id === selectedId ? 'pp-row--selected' : '',
                                            instrument.is_active ? '' : 'pp-row--inactive',
                                        ].join(' ')}
                                        onClick={() => select(instrument.id)}
                                    >
                                        <td className="pp-cell-strong">
                                            <button
                                                type="button"
                                                className="pp-link-btn"
                                                aria-expanded={instrument.id === selectedId}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    select(instrument.id);
                                                }}
                                            >
                                                {instrument.name}
                                            </button>
                                        </td>
                                        <td>{textOrDash(instrument.instrument_type_label)}</td>
                                        <td><span className="pp-code">{instrument.benchmark.code}</span></td>
                                        <td className="pp-nowrap">{unitLabel(instrument.currency, instrument.contract_unit)}</td>
                                        <td className="pp-cell-clamp" title={instrument.settlement_basis || undefined}>
                                            {textOrDash(instrument.settlement_basis)}
                                        </td>
                                        <td><ActiveStatus active={instrument.is_active} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {selected ? (
                        <InstrumentDetail
                            instrument={selected}
                            acting={acting}
                            actionError={actionError}
                            onToggle={() => toggleActive(selected)}
                            onEdit={() => setForm({ mode: 'edit', instrument: selected })}
                            onClose={() => select(selected.id)}
                        />
                    ) : null}
                </div>
            )}

            <p className="pp-footnote">
                <FontAwesomeIcon icon={faCircleInfo} /> Each instrument must be linked to an active benchmark.
            </p>

            {form ? (
                <InstrumentFormModal
                    instrument={form.mode === 'edit' ? form.instrument : null}
                    onClose={() => setForm(null)}
                    onSaved={handleSaved}
                />
            ) : null}
        </>
    );
};

export default PaperInstruments;
